import { LogoOpentofu, LogoTerraform } from "components/icons/generated";
import { RunWorkerMetricsProvider, RunWorkerMetricsState } from "types/generated";

import { SlowestItem, SlowestItemGroup } from "./Card/types";
import { getPhaseLabel, getRunObservabilityAttentionState } from "../utils";

const RESOURCES_GROUP_ID = "resources";
const HOOKS_GROUP_ID = "hooks";
const PROVIDERS_GROUP_ID = "providers";
const PROVIDER_REGISTRIES = [
  { prefix: "registry.opentofu.org/", icon: LogoOpentofu },
  { prefix: "registry.terraform.io/", icon: LogoTerraform },
];

const isMeasured = ({ seconds }: SlowestItem) => seconds >= 0;

const toStateGroups = (
  states: RunWorkerMetricsState[],
  groupId: string,
  toItems: (state: RunWorkerMetricsState) => SlowestItem[] | undefined
): SlowestItemGroup[] => {
  const attentionState = getRunObservabilityAttentionState(
    states.filter((state) => toItems(state))
  );

  return states.flatMap((state) => {
    const items = toItems(state)?.filter(isMeasured);

    return items
      ? [
          {
            id: `${groupId}-${state.runState}`,
            label: getPhaseLabel(state.runState),
            items,
            needsAttention: state === attentionState,
            phase: state.runState,
          },
        ]
      : [];
  });
};

export const getSlowestResourceGroups = (states: RunWorkerMetricsState[]): SlowestItemGroup[] =>
  toStateGroups(states, RESOURCES_GROUP_ID, ({ slowestResources }) =>
    slowestResources?.map(({ address, resourceType, durationSeconds, failed }) => ({
      title: address,
      subtitle: resourceType || undefined,
      seconds: durationSeconds,
      failed,
    }))
  );

export const getSlowestHookGroups = (states: RunWorkerMetricsState[]): SlowestItemGroup[] =>
  toStateGroups(states, HOOKS_GROUP_ID, ({ slowestHooks }) =>
    slowestHooks?.map(({ command, phase, durationSeconds }) => ({
      title: command,
      subtitle: phase || undefined,
      seconds: durationSeconds,
    }))
  );

const toProviderItem = ({
  address,
  installDurationSeconds,
}: RunWorkerMetricsProvider): SlowestItem => {
  const registry = PROVIDER_REGISTRIES.find(({ prefix }) => address.startsWith(prefix));

  if (registry) {
    return {
      title: address.slice(registry.prefix.length),
      icon: registry.icon,
      seconds: installDurationSeconds,
    };
  }

  return { title: address, seconds: installDurationSeconds };
};

export const getSlowestProviderGroups = (
  providers: RunWorkerMetricsProvider[]
): SlowestItemGroup[] => {
  const items = providers.map(toProviderItem).filter(isMeasured);

  return items.length > 0 ? [{ id: PROVIDERS_GROUP_ID, items }] : [];
};

export const getSlowestItemsScaleSeconds = (groups: SlowestItemGroup[]) =>
  Math.max(0, ...groups.flatMap(({ items }) => items.map(({ seconds }) => seconds)));
